import {
  Button,
  Container,
  Form,
  Header,
  SpaceBetween,
  Textarea,
  Spinner,
} from "@cloudscape-design/components-themed";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { createRef, useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../app/hook";
import { usePostConversionToolMutation } from "../../features/api";
import { Messages, getMessage } from "../../features/error/messages";
import { addNotification } from "../../features/error/notifications-slice";
import { getExt } from "../../utils/common";
import { formatLabel } from "../../utils/format";
import { FileUpload } from "../UI/file-upload/FileUpload";
import { showModal } from "../UI/modal/modal-slice";
import { COMMON_LABELS, COMMON_CONFIGS } from "../default-constants";
import { CONFIGS, CONTENTS, LABELS } from "./constants";
import ErrorTable, { MessageItem, getMessageItemList } from "./ErrorTable";
import { downloadBase64File } from "../../utils/downLoad";

export interface FileItem {
  label: string;
  dismissLabel: string;
}

/**
 * 画面
 */
export const Main = (props: any) => {
  const dispatch = useAppDispatch();

  // 加盟店項目提出用設定
  // inputRef
  const merchantInputRef = useRef<HTMLInputElement>(null);
  // アップロードファイル
  const [uploadMerchantFile, setUploadMerchantFile] = useState<File[]>();
  // エラーテキスト
  const [merchantErrorText, setMerchantErrorText] = useState("");

  // ドコモ項目用ファイル設定
  // inputRef
  const docomoInputRef = useRef<HTMLInputElement>(null);

  //ドコモ項目用ファイル
  const [uploadDocomoFile, setUploadDocomoFile] = useState<File>();
  // エラーテキスト
  const [docomoErrorText, setDocomoErrorText] = useState("");

  // 加盟店申請（包括）API設定
  const [uploadConversionToolFiles] = usePostConversionToolMutation();

  // スピナー表示処理用
  const [isSpinnerDisplay, setIsSpinnerDisplay] = useState(false);
  // ボタン非活性用
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  // エラーメッセージ表示処理用
  const [merchantErrorMessageList, setMerchantErrorMessageList] = useState<
    MessageItem[]
  >([]);
  const [docomoErrorMessageList, setDocomoErrorMessageList] = useState<
    MessageItem[]
  >([]);
  // ref を作成しスクロールさせたい場所にある Element にセット
  const corporateErrorRef = createRef<HTMLDivElement>();

  const storeErrorRef = createRef<HTMLDivElement>();

  // 加盟店項目用提出用ファイル設定
  const onChangeMerchantFile = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const fileList = e.target.files;
    if (!fileList) {
      return;
    }
    const files = Array.from(fileList);

    if (files !== undefined) {
      // 加盟店項目用アップロードファイル設定処理
      files.forEach((file) => {
        // アップロードされたファイルの拡張子が「.xlsx」以外の場合はアラートを出して終了する。
        if (
          CONFIGS.FILE_EXTENSION_XLSX !== getExt(file.name.toLowerCase()) &&
          CONFIGS.FILE_EXTENSION_XLSM !== getExt(file.name.toLowerCase())
        ) {
          const messageId = "KGRERR057" as keyof Messages;
          // アラート処理
          dispatch(
            addNotification({
              msgId: messageId,
              msgArgs: [],
            })
          );
        }
      });
    }
    // 送信するファイル情報を設定
    setUploadMerchantFile(files);

    // エラーテキストを設定
    setMerchantErrorText(LABELS.ERROR_TEXT_LABEL);
  };

  // ドコモ項目用提出用ファイル設定
  const onChangeDocomoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (!files?.[0]) {
      return;
    }

    // アップロードされたファイルの拡張子が「.xlsx」以外の場合はアラートを出して終了する。
    if (CONFIGS.FILE_EXTENSION_XLSX !== getExt(files[0].name.toLowerCase())) {
      const messageId = "KGRERR057" as keyof Messages;
      // アラート処理
      dispatch(
        addNotification({
          msgId: messageId,
          msgArgs: [],
        })
      );
      return;
    }
    // 送信するファイル情報を設定
    setUploadDocomoFile(files[0]);

    // エラーテキストを設定
    setDocomoErrorText(LABELS.ERROR_TEXT_LABEL);
  };

  // 加盟店用アップロードボタンイベント
  const merchantUploadOnClick = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    // ファイルアップロード処理
    merchantInputRef.current?.click();
  };

  // ドコモ用アップロードボタンイベント
  const docomoUploadOnClick = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    // ファイルアップロード処理
    docomoInputRef.current?.click();
  };

  // 申請ボタン押下処理
  const onClickButton = async () => {
    const fileData = new FormData();

    if (!inputCheck()) {
      return;
    }
    // 確認ダイアログ表示メッセージ作成
    let contentMessage = COMMON_LABELS.CONTENTS.APPLICATION_CONFIRM;

    if (uploadMerchantFile !== undefined) {
      // 加盟店用アップロードファイル設定処理
      let fileNameLabel = "";
      uploadMerchantFile.forEach((file) => {
        fileData.append(CONFIGS.FILE, file);
        fileData.append(CONFIGS.UPLOAD_TYPE, CONFIGS.MERCHANT);
        //確認ダイアログ表示メッセージ追記;
        fileNameLabel += CONTENTS.APPLICATION_CONFIRM_MERCHANT_FILE;
        fileNameLabel += file.name;
      });
      contentMessage += formatLabel(CONTENTS.APPLICATION_CONFIRM_MERCHANT, [
        fileNameLabel,
      ]);
    }
    if (uploadDocomoFile !== undefined) {
      // ドコモ項目用アップロードファイル設定処理
      fileData.append(CONFIGS.FILE, uploadDocomoFile);
      fileData.append(CONFIGS.UPLOAD_TYPE, CONFIGS.DOCOMO);
      //　確認ダイアログ表示メッセージ追記
      contentMessage += formatLabel(CONTENTS.APPLICATION_CONFIRM_DOCOMO, [
        uploadDocomoFile.name,
      ]);
    }

    //　確認ダイアログ表示
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: contentMessage,
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        positiveFn: () => {
          merchantApplication(fileData);
        },
      })
    );

    /** 同じファイルを連続アップロードできるように処理 */
    if (merchantInputRef.current) {
      merchantInputRef.current.value = "";
    }
    if (docomoInputRef.current) {
      docomoInputRef.current.value = "";
    }
  };

  // 入力チェック
  const inputCheck = () => {
    let errorFlag = true;
    // ファイル未設定
    if (uploadMerchantFile === undefined && uploadDocomoFile === undefined) {
      // ファイル未選択時のエラー表示
      const messageId = "KGRERR054" as keyof Messages;
      // エラーテキストを設定
      setMerchantErrorText(getMessage(messageId).label);
      setDocomoErrorText(getMessage(messageId).label);
      errorFlag = false;
    }
    //ドコモ項目ファイルのみ設定時
    if (uploadDocomoFile !== undefined) {
      if (uploadMerchantFile === undefined) {
        // ファイル未選択時のエラー表示
        const messageId = "KGRERR054" as keyof Messages;
        // エラーテキストを設定
        setMerchantErrorText(getMessage(messageId).label);
        errorFlag = false;
      }
    }
    //加盟店ファイル複数設定時ドコモ審査は設定不可
    if (uploadMerchantFile !== undefined && uploadMerchantFile?.length > 1) {
      if (uploadDocomoFile !== undefined) {
        // ファイル未選択時のエラー表示
        const messageId = "KGRERR139" as keyof Messages;
        // エラーテキストを設定
        setDocomoErrorText(getMessage(messageId).label);
        errorFlag = false;
      }
    }
    return errorFlag;
  };

  // 確認ボタンOK押下時の処理
  const merchantApplication = async (fileData: FormData) => {
    // スピナー起動
    setIsSpinnerDisplay(true);
    // ボタン非活性
    setIsButtonDisabled(true);

    // エラーテキストを初期化
    setDocomoErrorText("");
    setMerchantErrorText("");

    // 変換ツールアップロードファイルAPI呼び出し
    uploadConversionToolFiles(fileData)
      .unwrap()
      .then((res) => {
        if (undefined !== res.items && null !== res.items) {
          for (let item of res.items) {
            // ファイルダウンロード処理実行
            downloadBase64File(item);
          }
        }
        // エラー内容を初期化
        setMerchantErrorMessageList([]);
        setDocomoErrorMessageList([]);

        // アップロード後初期化
        setUploadMerchantFile(undefined);
        setUploadDocomoFile(undefined);

        if (COMMON_CONFIGS.API.STATUS_ERROR === res.status) {
          if (
            res.merchantErrorMessageList !== null &&
            res.merchantErrorMessageList.length !== 0
          ) {
            setMerchantErrorMessageList(
              getMessageItemList(res.merchantErrorMessageList)
            );
          }
          if (
            res.docomoErrorMessageList !== null &&
            res.docomoErrorMessageList.length !== 0
          ) {
            setDocomoErrorMessageList(
              getMessageItemList(res.docomoErrorMessageList)
            );
          }
        }
      })
      .finally(() => {
        // スピナー停止
        setIsSpinnerDisplay(false);
        // ボタン活性
        setIsButtonDisabled(false);
      });
  };

  // このコールバックを呼び出して ref.current.scrollIntoView() を呼び出してスクロール
  const scrollToBottomOfList = useCallback(() => {
    // 店舗用エラー内容にスクロール
    storeErrorRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });

    // 法人用エラー内容にスクロール
    corporateErrorRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corporateErrorRef, storeErrorRef]);

  useEffect(() => {
    // ページが描画されたらリストの末尾までスクロール
    scrollToBottomOfList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantErrorMessageList, docomoErrorMessageList]);

  return (
    <Container
      header={
        <Header variant="h2" description="">
          {LABELS.APPLICATION_FILE}
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="xxl">
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="l">
              {isSpinnerDisplay ? <Spinner size="big" /> : <></>}
              <Button
                iconAlign="right"
                onClick={onClickButton}
                variant="primary"
                disabled={isButtonDisabled}
              >
                {COMMON_LABELS.BTNS.UL}
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween direction="vertical" size="xxl">
            <FileUpload
              inputRef={merchantInputRef}
              label={LABELS.FOR_MERCHANT}
              name={"merchantFile"}
              placeholder={LABELS.FILE_PLACEHOLDER}
              accept={CONFIGS.FILE_EXTENSION_MERCHANT_ACCEPT}
              multiple={true}
              fileName={
                uploadMerchantFile
                  ? uploadMerchantFile.map((file) => file.name).join(",")
                  : ""
              }
              setUploadFiles={setUploadMerchantFile}
              errorText={merchantErrorText}
              setErrorText={setMerchantErrorText}
              onChange={onChangeMerchantFile}
              onUpload={merchantUploadOnClick}
            />
            <FileUpload
              inputRef={docomoInputRef}
              label={LABELS.FOR_DOCOMO}
              name={"docomoFile"}
              placeholder={LABELS.FILE_PLACEHOLDER}
              accept={CONFIGS.FILE_EXTENSION_XLSX}
              fileName={uploadDocomoFile ? uploadDocomoFile.name : ""}
              setUploadFile={setUploadDocomoFile}
              errorText={docomoErrorText}
              setErrorText={setDocomoErrorText}
              onChange={onChangeDocomoFile}
              onUpload={docomoUploadOnClick}
            />
          </SpaceBetween>
        </Form>
        <SpaceBetween direction="vertical" size="xxs">
          <Textarea
            value="加盟店申請書のみの場合は複数枚アップロード可能、ドコモ項目をアップロードする場合は加盟店申請書は一枚のみ"
            readOnly={true}
            rows={13}
          ></Textarea>
        </SpaceBetween>
      </SpaceBetween>
      <SpaceBetween direction="vertical" size="xxs">
        {merchantErrorMessageList.length !== 0 ? (
          <>
            <ErrorTable
              data={merchantErrorMessageList}
              title={LABELS.ERROR_TITLE_FOR_MERCHANT}
            />
            <div id="corporateError" ref={corporateErrorRef} />
          </>
        ) : (
          <></>
        )}
        {docomoErrorMessageList.length !== 0 ? (
          <>
            <ErrorTable
              data={docomoErrorMessageList}
              title={LABELS.ERROR_TITLE_FOR_DOCOMO}
            />
            <div id="storeError" ref={storeErrorRef} />
          </>
        ) : (
          <></>
        )}
      </SpaceBetween>
    </Container>
  );
};
